// The fetching half of an audit's accordion group (see WorkStepAuditGroup
// for the pre-fetch placeholder): a collapsible header row (audit name,
// work-step count, group select-all) followed by that audit's work-step rows.
// Mounted on the group's first expand and kept mounted across collapses, it
// owns the audit's paginated fetch scoped by `ops_audit_id`.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import { Checkbox } from "../../../../../components/ui/checkbox";
import { TableCell, TableRow } from "../../../../../components/ui/table";
import { useOptroImportWorkSteps } from "../../../../../hooks/useOptroImportWorkSteps";
import {
  OptroOpsAuditSummary,
  OptroWorkStepSummary,
} from "../../../../../models/audit/workStep";
import { SelectWorkStepRow } from "./SelectWorkStepRow";

const COLUMN_COUNT = 5;

interface WorkStepAuditGroupLoadedProps {
  audit: OptroOpsAuditSummary;
  search: string;
  isSelected: (externalRef: string) => boolean;
  onToggle: (workStep: OptroWorkStepSummary) => void;
  selectMany: (workSteps: OptroWorkStepSummary[]) => void;
  deselectMany: (externalRefs: string[]) => void;
  // True when activation came from the placeholder's select-all checkbox:
  // once the first page lands, the whole group gets selected.
  autoSelectAll: boolean;
}

export const WorkStepAuditGroupLoaded = ({
  audit,
  search,
  isSelected,
  onToggle,
  selectMany,
  deselectMany,
  autoSelectAll,
}: WorkStepAuditGroupLoadedProps) => {
  const [open, setOpen] = useState(true);

  const {
    workSteps,
    total,
    loading,
    loadingMore,
    error,
    retry,
    loadMore,
    hasMore,
    fetchAllRemaining,
  } = useOptroImportWorkSteps(search, {
    opsAuditId: audit.id,
    excludeLinked: false,
  });

  // Group select-all: checking selects every selectable (not yet imported)
  // work step in this audit matching the current search — fetching unloaded
  // pages first — and unchecking clears exactly this group's rows.
  const [selectingAll, setSelectingAll] = useState(false);
  const selectableLoaded = workSteps.filter(
    (w) => !w.linked && !w.importing && w.untestableStatus === null,
  );
  const someSelected = selectableLoaded.some((w) => isSelected(w.externalRef));
  const allLoadedSelected =
    selectableLoaded.length > 0 &&
    selectableLoaded.every((w) => isSelected(w.externalRef));
  const headerChecked: boolean | "indeterminate" = !someSelected
    ? false
    : !hasMore && allLoadedSelected
      ? true
      : "indeterminate";

  const onToggleAll = async () => {
    if (someSelected) {
      deselectMany(workSteps.map((w) => w.externalRef));
      return;
    }
    setSelectingAll(true);
    const result = await fetchAllRemaining();
    setSelectingAll(false);
    if (result.status === "ok") {
      selectMany(
        result.workSteps.filter(
          (w) => !w.linked && !w.importing && w.untestableStatus === null,
        ),
      );
    } else if (result.status === "error") {
      toast.error(`Couldn't load all work steps for ${audit.name}. Try again.`);
    }
    // "superseded" (search changed mid-fetch): do nothing.
  };

  // Activation-by-checkbox: the placeholder can't select rows it never
  // fetched, so it hands the intent here and the select-all runs once the
  // first page has landed.
  const autoSelectAllDoneRef = useRef(false);
  useEffect(() => {
    if (!autoSelectAll || autoSelectAllDoneRef.current || loading || error) {
      return;
    }
    autoSelectAllDoneRef.current = true;
    void onToggleAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot, ref-guarded
  }, [autoSelectAll, loading, error]);

  // Per-group infinite scroll observer, same wiring as the audits table.
  const observer = useRef<IntersectionObserver | null>(null);
  const lastRowRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (loading || loadingMore) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, hasMore, loadMore],
  );

  return (
    <>
      <TableRow
        className="bg-stone-50 hover:bg-stone-100 cursor-pointer"
        onClick={() => setOpen((prev) => !prev)}
      >
        <TableCell onClick={(e) => e.stopPropagation()}>
          {selectingAll ? (
            <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
          ) : (
            <Checkbox
              checked={headerChecked}
              onCheckedChange={() => void onToggleAll()}
              aria-label={`Select all work steps in ${audit.name}`}
            />
          )}
        </TableCell>
        <TableCell colSpan={COLUMN_COUNT - 1} className="font-semibold">
          <div className="flex items-center gap-2">
            {open ? (
              <ChevronDown className="h-4 w-4 text-stone-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-stone-500" />
            )}
            <span>{audit.name}</span>
            {!loading && (
              <span className="text-sm font-normal text-stone-500">
                {total} work step{total === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </TableCell>
      </TableRow>
      {open && loading && (
        <TableRow>
          <TableCell colSpan={COLUMN_COUNT} className="py-4 text-center">
            <Loader2 className="mx-auto h-4 w-4 animate-spin text-stone-400" />
          </TableCell>
        </TableRow>
      )}
      {open && !loading && error && (
        <TableRow>
          <TableCell
            colSpan={COLUMN_COUNT}
            className="py-4 text-center text-sm text-stone-500"
          >
            Could not load work steps for this audit.
            <Button variant="link" size="sm" className="px-1" onClick={retry}>
              Retry
            </Button>
          </TableCell>
        </TableRow>
      )}
      {open && !loading && !error && workSteps.length === 0 && (
        <TableRow>
          <TableCell
            colSpan={COLUMN_COUNT}
            className="py-4 text-center text-sm text-stone-500"
          >
            {search.trim() !== ""
              ? "No work steps match your search in this audit."
              : "No work steps in this audit."}
          </TableCell>
        </TableRow>
      )}
      {open &&
        !loading &&
        !error &&
        workSteps.map((workStep, index) => (
          <SelectWorkStepRow
            key={workStep.externalRef}
            workStep={workStep}
            checked={isSelected(workStep.externalRef)}
            onToggle={onToggle}
            ref={index === workSteps.length - 1 ? lastRowRef : undefined}
          />
        ))}
      {open && loadingMore && (
        <TableRow>
          <TableCell colSpan={COLUMN_COUNT} className="py-4 text-center">
            <Loader2 className="mx-auto h-4 w-4 animate-spin text-stone-400" />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
